import classNames from 'classnames';
import parsePhoneNumber, { AsYouType, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import React, { useEffect, useRef, useState } from 'react';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { sanitizePhoneNumber } from 'Validators/Functions/HumanWritten/Phone';
import ErrorMessage from './ErrorMessage';

// Full country names for the dropdown, when the runtime supports Intl.DisplayNames (all modern
// browsers). Falls back to the ISO code otherwise.
const hasDisplayNames = typeof (Intl as any).DisplayNames === 'function';
const regionNames: { of(code: string): string | undefined } | null = hasDisplayNames
	? new (Intl as any).DisplayNames(['en'], { type: 'region' })
	: null;

// Flag emoji derived from the 2-letter country code (regional-indicator symbols) — no assets needed.
const flagEmoji = (code: string) =>
	code.toUpperCase().replace(/./g, ch => String.fromCodePoint(127397 + ch.charCodeAt(0)));

interface CountryOption {
	code: CountryCode;
	name: string;
	calling: string;
}

const COUNTRY_OPTIONS: CountryOption[] = getCountries()
	.map(code => ({ code, name: regionNames?.of(code) ?? code, calling: getCountryCallingCode(code) }))
	.sort((a, b) => a.name.localeCompare(b.name));

export interface PhoneInputProps {
	/** The phone number in E.164 (e.g. "+61412345678"). Legacy national values are accepted too. */
	value?: string | null;
	/** Called with the E.164 value ("" when empty). */
	onChange: (value: string) => void;
	label?: React.ReactNode;
	error?: string;
	required?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	placeholder?: string;
	id?: string;
	/** Overrides the initial country; defaults to the operator's configured country. */
	defaultCountry?: CountryCode;
	tooltip?: string;
	className?: string;
	'data-test-id'?: string;
}

/**
 * The vertical band the dropdown can be seen in: the window, narrowed by any scrolling ancestor. Inside
 * a modal the body scrolls and clips what overflows it, so measuring against the window alone opened the
 * list downwards into the footer, where it was cut off.
 */
function getVisibleBounds(element: HTMLElement): { top: number; bottom: number } {
	let top = 0;
	let bottom = window.innerHeight;
	for (let node = element.parentElement; node; node = node.parentElement) {
		const { overflowY } = getComputedStyle(node);
		if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
			const bounds = node.getBoundingClientRect();
			top = Math.max(top, bounds.top);
			bottom = Math.min(bottom, bounds.bottom);
		}
	}
	return { top, bottom };
}

interface ParsedValue {
	country: CountryCode;
	national: string;
}

function parseValue(value: string | null | undefined, fallback: CountryCode): ParsedValue {
	if (!value) {
		return { country: fallback, national: '' };
	}
	if (value.startsWith('+')) {
		try {
			const parsed = parsePhoneNumber(value);
			if (parsed) {
				return { country: parsed.country ?? fallback, national: parsed.formatNational() };
			}
		} catch {
			/* fall through to raw */
		}
		return { country: fallback, national: value };
	}
	// Legacy value stored without a country code — treat it as a national number in the default country.
	return { country: fallback, national: value };
}

/**
 * International phone-number input: a country/area-code selector plus a national-number field.
 * Emits the value in E.164 so it can be stored in the existing single phone string field.
 * Uses the already-installed libphonenumber-js for the country list, formatting and parsing.
 */
export function PhoneInput({
	value,
	onChange,
	label,
	error,
	required,
	disabled,
	readOnly,
	placeholder,
	id,
	defaultCountry,
	tooltip,
	className,
	...rest
}: PhoneInputProps) {
	const fallbackCountry = defaultCountry ?? whiteLabelStore.countryCode;
	const inputId = id || (typeof label === 'string' ? label.replace(/\s+/g, '-').toLowerCase() : undefined);
	const testId = (rest as { 'data-test-id'?: string })['data-test-id'];

	const initial = parseValue(value, fallbackCountry);
	const [country, setCountry] = useState<CountryCode>(initial.country);
	const [national, setNational] = useState<string>(initial.national);
	// Tracks the last value we emitted, so an external change re-syncs but our own edits don't loop.
	const lastEmitted = useRef<string>(value ?? '');

	useEffect(() => {
		if ((value ?? '') === lastEmitted.current) {
			return;
		}
		const parsed = parseValue(value, fallbackCountry);
		setCountry(parsed.country);
		setNational(parsed.national);
		lastEmitted.current = value ?? '';
		// Only re-sync on external value changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	// Custom country dropdown: the collapsed control shows only the calling code, while the open
	// list shows flag + country + code. A native <select> can't render different collapsed vs list
	// text, so this is a lightweight popover with click-outside / Escape to close.
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	// Flip the dropdown above the field when there isn't enough room below, and cap its height to
	// whatever space is available so it never runs off the top or bottom of the screen.
	const [dropUp, setDropUp] = useState(false);
	const [menuMaxHeight, setMenuMaxHeight] = useState(320);
	const countryRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);

	const normalisedSearch = search.trim().toLowerCase();
	const filteredCountries = normalisedSearch
		? COUNTRY_OPTIONS.filter(
				o =>
					o.name.toLowerCase().includes(normalisedSearch) ||
					o.code.toLowerCase().includes(normalisedSearch) ||
					`+${o.calling}`.includes(normalisedSearch),
			)
		: COUNTRY_OPTIONS;

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		// Decide whether to open up or down based on available space, and cap the height to fit.
		const rect = countryRef.current?.getBoundingClientRect();
		if (rect && countryRef.current) {
			const { top, bottom } = getVisibleBounds(countryRef.current);
			const spaceBelow = bottom - rect.bottom;
			const spaceAbove = rect.top - top;
			const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
			setDropUp(openUp);
			setMenuMaxHeight(Math.max(160, Math.min(320, (openUp ? spaceAbove : spaceBelow) - 12)));
		}
		// Focus the search box when the dropdown opens (ref-based, not the autoFocus prop).
		searchRef.current?.focus();
		const onDocMouseDown = (e: MouseEvent) => {
			if (countryRef.current && !countryRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', onDocMouseDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('mousedown', onDocMouseDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [open]);

	const emit = (nextCountry: CountryCode, nextNational: string) => {
		const digits = nextNational.replace(/[^\d]/g, '');
		let e164 = '';
		if (digits) {
			try {
				const parsed = parsePhoneNumber(nextNational, nextCountry);
				e164 = parsed ? parsed.number : `+${getCountryCallingCode(nextCountry)}${digits}`;
			} catch {
				e164 = `+${getCountryCallingCode(nextCountry)}${digits}`;
			}
		}
		lastEmitted.current = e164;
		onChange(e164);
	};

	const handleCountry = (next: CountryCode) => {
		setCountry(next);
		emit(next, national);
	};

	const handleNational = (raw: string) => {
		const clean = sanitizePhoneNumber(raw);
		setNational(clean);
		emit(country, clean);
	};

	// Tidy the displayed national number on blur without changing the emitted E.164 value.
	const handleBlur = () => {
		if (!national.trim()) {
			return;
		}
		try {
			const parsed = parsePhoneNumber(national, country);
			setNational(parsed ? parsed.formatNational() : new AsYouType(country).input(national));
		} catch {
			/* leave as typed */
		}
	};

	return (
		<div className={classNames('flex flex-col gap-1 w-full', className)}>
			{label && (
				<div className="flex items-center gap-1">
					<label htmlFor={inputId} className="text-sm text-gray-800">
						{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
					</label>
					{tooltip && (
						<div className="relative group">
							<span className="material-symbols-outlined text-gray-400 text-sm leading-none cursor-help">
								help
							</span>
							<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none">
								{tooltip}
							</div>
						</div>
					)}
				</div>
			)}
			<div
				className={classNames(
					'flex items-stretch rounded-md border transition-all custom-input',
					// eslint-disable-next-line no-nested-ternary
					disabled
						? 'border-gray-300 bg-gray-100 cursor-not-allowed'
						: error
							? 'border-red-400 bg-red-50 focus-within:ring-1 focus-within:ring-[#ef4444]'
							: 'border-gray-300 hover:border-gray-400 focus-within:border-[var(--colour-brand-primary)] focus-within:ring-1 focus-within:ring-[var(--colour-brand-primary)]',
					readOnly && 'bg-gray-50',
				)}
			>
				<div ref={countryRef} className="flex relative items-center border-r border-gray-200">
					<button
						type="button"
						aria-label="Country calling code"
						aria-haspopup="listbox"
						aria-expanded={open}
						disabled={disabled || readOnly}
						onClick={() => {
							setSearch('');
							setOpen(prev => !prev);
						}}
						// !h-auto overrides any ambient `button { height }` rule (e.g. the login form's 60px).
						className="flex gap-1 items-center py-3 pr-2 pl-3 !h-auto text-sm whitespace-nowrap rounded-l-md outline-none disabled:cursor-not-allowed"
					>
						+{getCountryCallingCode(country)}
						{/* Fixed-size SVG chevron (not a font icon) so it can't inflate the row height. */}
						<svg
							className="shrink-0 w-4 h-4 text-gray-400"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							aria-hidden="true"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
						</svg>
					</button>
					{open && (
						<div
							style={{ maxHeight: menuMaxHeight }}
							className={classNames(
								'flex overflow-hidden absolute left-0 z-20 flex-col w-64 max-w-[calc(100vw-2rem)] bg-white rounded-md border border-gray-200 shadow-lg',
								dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
							)}
						>
							{/* custom-input opts the search box out of the global legacy input styling
							    (which would force width:auto and its own border). */}
							<div className="p-2 border-b border-gray-100 custom-input">
								<input
									ref={searchRef}
									type="text"
									value={search}
									onChange={e => setSearch(e.target.value)}
									placeholder="Search country or code"
									className="block box-border px-3 py-2 w-full text-sm rounded-md border border-gray-300 outline-none focus:border-[var(--colour-brand-primary)]"
								/>
							</div>
							<ul className="overflow-auto flex-1">
								{filteredCountries.map(opt => (
									<li key={opt.code}>
										<button
											type="button"
											onClick={() => {
												handleCountry(opt.code);
												setOpen(false);
											}}
											className={classNames(
												'flex gap-2 items-center px-3 py-2 !h-auto w-full text-sm text-left hover:bg-gray-100',
												opt.code === country && 'bg-gray-50',
											)}
										>
											<span>{flagEmoji(opt.code)}</span>
											<span className="flex-1 truncate">{opt.name}</span>
											<span className="text-gray-500">+{opt.calling}</span>
										</button>
									</li>
								))}
								{filteredCountries.length === 0 && (
									<li className="px-3 py-2 text-sm text-gray-400">No matches</li>
								)}
							</ul>
						</div>
					)}
				</div>
				<input
					id={inputId}
					type="tel"
					inputMode="tel"
					autoComplete="tel-national"
					value={national}
					placeholder={placeholder}
					disabled={disabled}
					readOnly={readOnly}
					data-test-id={testId}
					onChange={e => handleNational(e.target.value)}
					onBlur={handleBlur}
					className={classNames(
						'flex-1 px-3 py-3 w-full text-sm bg-transparent outline-none',
						'placeholder:text-gray-400',
						disabled && 'text-gray-500 cursor-not-allowed',
						readOnly && 'text-gray-600',
					)}
				/>
			</div>
			<ErrorMessage error={error} />
		</div>
	);
}
